import React, { useEffect, useState } from "react";
import {
  StyledAchievementSelect,
  StyledBottomDivTable,
  StyledBoldError,
  StyledChallengeField,
  StyledChallengeGrid,
  StyledChallengeLabel,
  StyledFinalCalibrationError,
  StyledGoalCard,
  StyledGoalCardBody,
  StyledGoalCardHeader,
  StyledGoalCardHeaderRight,
  StyledGoalsErrorWrapper,
  StyledGoalsList,
  StyledGoalsPage,
  StyledGoalsScroll,
  StyledAchievementPillContainer,
} from "./Goals.styled";
import { Pill } from "../../../../../components/Pill/Pill";
import { ReactComponent as InfoIcon } from "../../../../../icons/info.svg";
import { ReactComponent as PlusIcon } from "../../../../../icons/add.svg";
import { ReactComponent as ChevronUpIcon } from "../../../../../icons/chevronUp.svg";
import { ReactComponent as ChevronDownIcon } from "../../../../../icons/chevronDown.svg";
import { IconManager } from "../../../../../components/core/IconManager/IconManager";
import { FontWeightEnum, IconSizeEnum } from "../../../../../types/AppTypes";
import {
  ACHIEVEMENT_PERCENTAGE_DISABLED_TOOLTIP,
  ACHIEVEMENT_PERCENTAGE_ENABLED_TOOLTIP,
  ACTION_LABEL,
  ACTION_TOOLTIP,
  AVG_ACHIEVEMENT_LABEL,
  CONTEST_LABEL,
  CONTEST_TOOLTIP,
  DESCRIPTION_LABEL,
  EMPTY_GOALS_MESSAGE,
  EMPTY_STRING,
  FINAL_CALIBRATION_ERROR_END,
  FINAL_CALIBRATION_ERROR_START,
  GOAL_LABEL,
  HIGH_IMPACT_LABEL,
  IMPACT_LABEL,
  IMPACT_TOOLTIP,
  LOW_IMPACT_LABEL,
  MEDIUM_IMPACT_LABEL,
  NO_IMPACT_LABEL,
  PERCENT_REACHED_GOALS_LABEL,
  TIME_LABEL,
  TIME_TOOLTIP,
} from "../../../../../constants/Dictionary";
import {
  Select,
  SelectOption,
  SelectVariantEnum,
} from "../../../../../components/core/Select/Select";
import TextArea from "../../../../../components/core/TextArea/TextArea";
import { InputSizes } from "../../../../../components/core/Input/Input";
import { TextAreaBox } from "../../../../../components/TextAreaBox/TextAreaBox";
import * as yup from "yup";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppSelector } from "../../../../../hooks/store.hook";
import { FormField } from "../../../../../components/core/FormField/FormField";
import {
  BodyTyp,
  CaptionsTyp,
} from "../../../../../components/Typography/Typography";
import { theme } from "../../../../../constants/StyledComponents";
import {
  useCmSheetGoals,
  useUpdateCmSheetGoals,
} from "../../../../../hooks/cmSheet/goals.hook";
import { Loader } from "../../../../../components/core/Loader/Loader";
import { CollaboratorCmSheetGoal } from "../../../../../types/api/collaborators.type";
import { compareDataGeneric } from "../../../../../utils/comparing.utils";
import { CmSheetFooter } from "../Components/CmSheetFooter/CmSheetFooter";
import goalsImage from "../../../../../images/goals.png";
import { EmptyState } from "../../../../../components/EmptyState/EmptyState";
import {
  Button,
  ButtonSizes,
  ButtonVariants,
} from "../../../../../components/core/Button/Button";
import { Tooltip } from "../../../../../components/core/Tooltip/Tooltip";
import { StyledHideOrShowContainer } from "../SchedaCM.styled";
import {
  CmSheetStateEnum,
  CHARACTER_MAX_LENGTH,
  PermissionsEnum,
} from "../../../../../constants/AppConstants";
import { CmSheetSectionProps } from "../SchedaCM";
import { roleHasPermission } from "../../../../../utils/roleAuth.utils";
import { currentStateIsMoreEqualThanTargetState } from "../../../../../utils/cmSheet.utils";
import { StatCard } from "../../../../../components/StatCard/StatCard";
import { ReactComponent as TargetIcon } from "../../../../../icons/target.svg";
import { ReactComponent as ImpactIcon } from "../../../../../icons/impact.svg";
import { IMPACT_RANGES } from "../../../../../constants/PotentialMatrixConstants";

type Item = {
  id?: number | null;
  time?: number | null;
  contest?: number | null;
  action?: number | null;
  goal?: string;
  achievementPercent?: number | null;
};

type FormData = {
  items: Item[];
};

const challengeFields = [
  {
    key: "action",
    label: ACTION_LABEL,
    tooltip: ACTION_TOOLTIP,
    dimensionKey: "actionChallenge",
  },
  {
    key: "contest",
    label: CONTEST_LABEL,
    tooltip: CONTEST_TOOLTIP,
    dimensionKey: "contestChallenge",
  },
  {
    key: "time",
    label: TIME_LABEL,
    tooltip: TIME_TOOLTIP,
    dimensionKey: "timeChallenge",
  },
] as const;

export const Goals = ({
  footer,
  cmSheetInfo,
  collaborator,
  cmSheetStateMachineInfo,
  showFooter,
  isMyProfile,
  isInterim,
  edit,
  setEdit,
}: CmSheetSectionProps) => {
  const { config } = useAppSelector((state) => state.config);
  const { profile } = useAppSelector((state) => state.user);

  const [hasData, setHasData] = useState<boolean | null>(null);

  const selectedYearConfig = config.find(
    (item) => item.year === cmSheetInfo.configYear,
  );

  const { data, isFetching } = useCmSheetGoals(cmSheetInfo.id);

  const { updateGoals, isUpdating: updating } = useUpdateCmSheetGoals(
    cmSheetInfo.id,
    collaborator.id,
  );

  const MIN_GOALS_COUNT = 3;

  const newGoal = {
    id: null,
    time: null,
    contest: null,
    action: null,
    goal: "",
    achievementPercent: null,
  };

  const createEmptyGoals = (count: number) =>
    Array.from({ length: count }, () => ({ ...newGoal }));

  const schema = yup.object().shape({
    items: yup
      .array()
      .required()
      .of(
        yup.object().shape({
          goal: yup.string(),
          time: yup.number().nullable(),
          contest: yup.number().nullable(),
          action: yup.number().nullable(),
          achievementPercent: yup.number().nullable(),
        }),
      ),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const { fields } = useFieldArray({
    control,
    name: "items",
    keyName: "rowId",
  });

  const onSubmit = async (formData: FormData) => {
    try {
      const compareData = formData.items.map((item) => ({
        id: item.id,
        timeChallenge: item.time,
        contestChallenge: item.contest,
        actionChallenge: item.action,
        goal: item.goal,
        achievementPercent: item.achievementPercent,
      }));
      const result = compareDataGeneric<CollaboratorCmSheetGoal>(
        data?.goals ?? [],
        compareData,
        ["impact"],
      );
      await updateGoals(result);
      setHasData(null);
    } finally {
      setEdit(false);
    }
  };

  const editAchievementPercentageIsEnabled = () => {
    if (roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS)) {
      return true;
    }

    return (
      cmSheetStateMachineInfo.currentState ===
        CmSheetStateEnum.INTERMEDIATE_EVALUATION ||
      (roleHasPermission(profile, PermissionsEnum.EDIT_OWN_CM_SHEET) &&
        cmSheetInfo.careerManagerId === profile.id &&
        (cmSheetStateMachineInfo.currentState ===
          CmSheetStateEnum.FINAL_FEEDBACK ||
          cmSheetStateMachineInfo.currentState ===
            CmSheetStateEnum.FINAL_CALIBRATION))
    );
  };

  const viewAchievementPercentageIsEnabled = () => {
    if (
      isMyProfile &&
      currentStateIsMoreEqualThanTargetState(
        cmSheetStateMachineInfo.currentState,
        CmSheetStateEnum.FINAL_FEEDBACK,
      ) &&
      cmSheetStateMachineInfo.isRevisionRequestSent
    ) {
      return true;
    }

    if (
      !isMyProfile &&
      (cmSheetInfo.careerManagerId === profile.id ||
        roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS) ||
        roleHasPermission(
          profile,
          PermissionsEnum.VIEW_GOAL_PERCENTAGE_ACHIEVEMENT,
        ))
    ) {
      return true;
    }

    return false;
  };

  const fieldIsDisabled = () => {
    if (
      roleHasPermission(profile, PermissionsEnum.EDIT_OWN_CM_SHEET) &&
      cmSheetInfo.careerManagerId === profile.id &&
      [
        CmSheetStateEnum.FINAL_CALIBRATION,
        CmSheetStateEnum.FINAL_FEEDBACK,
      ].includes(cmSheetStateMachineInfo.currentState)
    ) {
      return true;
    }

    return false;
  };

  const showFinalCalibrationError = () => {
    const canViewCalibrationError =
      roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS) ||
      roleHasPermission(profile, PermissionsEnum.EDIT_OWN_CM_SHEET);

    return (
      canViewCalibrationError &&
      data?.finalDimensionPercentage != null &&
      data.finalDimensionPercentage !== data.proposedDimensionPercentage
    );
  };

  const finalDimensionPercentage = (
    <StyledBoldError>{data?.finalDimensionPercentage}%</StyledBoldError>
  );

  const [expandedGoals, setExpandedGoals] = useState<number[]>([]);

  const toggleGoal = (index: number) => {
    setExpandedGoals((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const showLoader = isFetching && hasData === null;

  useEffect(() => {
    if (setEdit) {
      setEdit(false);
    }
    setHasData(null);
  }, [cmSheetInfo]);

  useEffect(() => {
    if (!isFetching) {
      if (data && data.goals.length > 0) {
        const mappedGoals = data.goals.map((item) => ({
          id: item.id,
          time: item.timeChallenge ?? null,
          contest: item.contestChallenge ?? null,
          action: item.actionChallenge ?? null,
          goal: item.goal ?? "",
          achievementPercent: item.achievementPercent ?? null,
        }));

        const items = [
          ...mappedGoals,
          ...createEmptyGoals(
            Math.max(0, MIN_GOALS_COUNT - mappedGoals.length),
          ),
        ];

        reset({ items });
      } else if (!isFetching && edit) {
        const items = createEmptyGoals(MIN_GOALS_COUNT);
        reset({ items });
      }

      if (data && hasData === null) {
        const newhasData = data.goals.length > 0;
        setHasData(newhasData);
      }
    }
  }, [data, selectedYearConfig, hasData, edit, isFetching, reset]);

  const canEdit = () => {
    if (!isMyProfile) {
      if (roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS)) {
        return true;
      }

      if (
        roleHasPermission(profile, PermissionsEnum.EDIT_OWN_CM_SHEET) &&
        profile.id === cmSheetInfo.careerManagerId &&
        [
          CmSheetStateEnum.DRAFT,
          CmSheetStateEnum.INTERMEDIATE_EVALUATION,
          CmSheetStateEnum.FINAL_FEEDBACK,
          CmSheetStateEnum.FINAL_CALIBRATION,
        ].includes(cmSheetStateMachineInfo.currentState)
      ) {
        return true;
      }
    }

    return false;
  };

  const emptyGoalPlaceholders: CollaboratorCmSheetGoal[] = Array.from(
    { length: MIN_GOALS_COUNT },
    (_, index) =>
      ({
        id: -index - 1,
        goal: "",
        timeChallenge: null,
        contestChallenge: null,
        actionChallenge: null,
        impact: 0,
        achievementPercent: null,
      }) as unknown as CollaboratorCmSheetGoal,
  );

  const showEmptyState = !isFetching && !hasData && !edit && canEdit();
  const showPlaceholderGoals = !isFetching && !hasData && !edit && !canEdit();
  const showPage = hasData === true || edit || showPlaceholderGoals;

  const readGoals = [
    ...(data?.goals ?? []),
    ...emptyGoalPlaceholders.slice(
      0,
      Math.max(0, MIN_GOALS_COUNT - (data?.goals?.length ?? 0)),
    ),
  ];

  const onSetEdit = (value: boolean) => {
    setEdit(value);
    if (value) {
      setExpandedGoals([0, 1, 2]);
    } else {
      setExpandedGoals([]);
    }
  };

  useEffect(() => {
    footer(
      <CmSheetFooter
        form="collaborator-goals-form"
        disabledEditButton={
          !isDirty || updating || (!hasData && fields.length === 0)
        }
        edit={edit}
        showEdit={canEdit() && (!!hasData || edit)}
        setEdit={onSetEdit}
        showFooter={showFooter}
        cmSheetInfo={cmSheetInfo}
        cmSheetStateMachineInfo={cmSheetStateMachineInfo}
        isMyProfile={isMyProfile}
        isInterim={isInterim}
      />,
    );
  }, [
    isDirty,
    edit,
    hasData,
    isFetching,
    updating,
    cmSheetInfo,
    cmSheetStateMachineInfo,
    fields,
  ]);

  const PERCENTAGE_OPTIONS = Array.from({ length: 20 }, (_, i) => {
    const value = (i + 1) * 5;
    return {
      label: `${value}%`,
      value,
    };
  });

  const evaluationOptions: { key: string; values: SelectOption<number>[] }[] = [
    {
      key: "actionChallenge",
      values: [
        {
          label: NO_IMPACT_LABEL,
          value: 0,
          description: "Nessuna sfida rilevata",
        },
        {
          label: LOW_IMPACT_LABEL,
          value: 1,
          description:
            "Attività esecutive, procedure definite, margine decisionale limitato nel proprio perimetro",
        },
        {
          label: MEDIUM_IMPACT_LABEL,
          value: 2,
          description:
            "Scelte operative autonome all’interno del proprio perimetro",
        },
        {
          label: HIGH_IMPACT_LABEL,
          value: 3,
          description:
            "Decisioni complesse, ambigue, con impatto rilevante sugli outcome del proprio perimetro",
        },
      ],
    },
    {
      key: "contestChallenge",
      values: [
        {
          label: NO_IMPACT_LABEL,
          value: 0,
          description: "Nessuna sfida rilevata",
        },
        {
          label: LOW_IMPACT_LABEL,
          value: 1,
          description: "Contesto stabile, prevedibile, ben strutturato",
        },
        {
          label: MEDIUM_IMPACT_LABEL,
          value: 2,
          description: "Interazioni multiple, variabilità gestibile",
        },
        {
          label: HIGH_IMPACT_LABEL,
          value: 3,
          description:
            "Elevata incertezza, cambiamenti frequenti, forte esposizione a fattori esterni",
        },
      ],
    },
    {
      key: "timeChallenge",
      values: [
        {
          label: NO_IMPACT_LABEL,
          value: 0,
          description: "Nessuna sfida rilevata",
        },
        {
          label: LOW_IMPACT_LABEL,
          value: 1,
          description: "Tempi pianificati, carico prevedibile",
        },
        {
          label: MEDIUM_IMPACT_LABEL,
          value: 2,
          description: "Scadenze multiple, priorità bilanciate",
        },
        {
          label: HIGH_IMPACT_LABEL,
          value: 3,
          description:
            "Urgenze frequenti, scadenze concorrenti, ripianificazione continua",
        },
      ],
    },
  ];

  const getEvaluationLabel = (key: string, value?: number | null): string => {
    const dimension = evaluationOptions.find((option) => option.key === key);
    if (!dimension) {
      return EMPTY_STRING;
    }
    const dimensionValue = dimension.values.find((v) => v.value === value);
    if (!dimensionValue) {
      return EMPTY_STRING;
    }
    return isMyProfile
      ? dimensionValue.label
      : `${dimensionValue.label} - ${value}`;
  };

  const getDimensionOptions = (key: string): SelectOption<number>[] => {
    const dimension = evaluationOptions.find((option) => option.key === key);
    if (!dimension) {
      return [];
    }
    return dimension.values.map((value) => ({
      ...value,
      label: `${value.label} - ${value.value}`,
    }));
  };

  const getImpactValue = (
    action?: number | null,
    contest?: number | null,
    time?: number | null,
  ): number => {
    const result = (action ?? 0) + (contest ?? 0) + (time ?? 0);
    return result;
  };

  const watchedItems = watch("items");

  const sumOfImpacts =
    watchedItems?.length > 0
      ? watchedItems.reduce((acc, item) => {
          return (
            acc +
            getImpactValue(item.action ?? 0, item.contest ?? 0, item.time ?? 0)
          );
        }, 0)
      : 0;

  const getImpactRangeLabel = (impact: number) => {
    return IMPACT_RANGES.find(
      (range) => impact >= range.min && impact <= range.max,
    )?.label;
  };

  const getImpactRangeDescription = (impact: number) => {
    return IMPACT_RANGES.find(
      (range) => impact >= range.min && impact <= range.max,
    )?.description;
  };

  const totalImpact = edit ? (sumOfImpacts ?? 0) : (data?.totalImpact ?? 0);

  const isOutOfRange = () => {
    return (
      totalImpact < IMPACT_RANGES[0].min ||
      totalImpact > IMPACT_RANGES[IMPACT_RANGES.length - 1].max
    );
  };

  const showBottomBar = viewAchievementPercentageIsEnabled() || !isOutOfRange();

  const calculateAvgAchievementPercentage = (): number | null => {
    let result =
      watchedItems?.length > 0
        ? watchedItems.reduce((acc, item) => {
            return (
              acc +
              (item.achievementPercent ?? 0) *
                getImpactValue(item.action, item.contest, item.time)
            );
          }, 0) / sumOfImpacts
        : 0;

    if (Number.isNaN(result)) {
      return null;
    }

    result = Math.round(result);

    const tens = Math.floor(result / 10) * 10;
    const units = result % 10;

    if (units < 3) {
      return tens;
    }
    if (units < 7) {
      return tens + 5;
    }
    return tens + 10;
  };

  const getAvgAchievementPercentage = (): string => {
    if (edit) {
      return `${calculateAvgAchievementPercentage() || EMPTY_STRING}%`;
    }

    return data?.proposedDimensionPercentage
      ? `${data.proposedDimensionPercentage}%`
      : EMPTY_STRING;
  };

  return (
    <>
      {showLoader && <Loader loading={true} className="cm-sheet-loader" />}
      {showEmptyState && (
        <div className="cm-sheet-empty-state">
          <EmptyState
            label={canEdit() ? EMPTY_GOALS_MESSAGE : ""}
            imageSrc={goalsImage}
          >
            {canEdit() && (
              <Button
                label={GOAL_LABEL}
                variant={ButtonVariants.SECONDARY}
                size={ButtonSizes.REGULAR}
                icon={<PlusIcon />}
                onClick={() => onSetEdit(true)}
              />
            )}
          </EmptyState>
        </div>
      )}
      <StyledHideOrShowContainer $show={showPage}>
        <Loader loading={updating}>
          <StyledGoalsPage $hasFooter={showFooter}>
            <StyledGoalsScroll>
              <StyledHideOrShowContainer $show={edit}>
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  id="collaborator-goals-form"
                >
                  <StyledGoalsList>
                    {fields.map((field, index) => {
                      const isExpanded = expandedGoals.includes(index);
                      const impact = getImpactValue(
                        watchedItems?.[index]?.action ?? 0,
                        watchedItems?.[index]?.contest ?? 0,
                        watchedItems?.[index]?.time ?? 0,
                      );

                      return (
                        <StyledGoalCard
                          key={field.rowId}
                          data-testid={`goal-card-${index}`}
                        >
                          <input
                            type="hidden"
                            {...register(`items.${index}.id`)}
                          />
                          <StyledGoalCardHeader>
                            <BodyTyp
                              variant="1"
                              fontWeight={FontWeightEnum.SEMIBOLD}
                            >
                              {GOAL_LABEL} {index + 1}
                            </BodyTyp>
                            <StyledGoalCardHeaderRight>
                              {!isMyProfile && (
                                <Tooltip message={IMPACT_TOOLTIP}>
                                  <Pill
                                    data-testid={`goal-impact-${index}`}
                                    status="active"
                                    label={`${IMPACT_LABEL}: ${impact}`}
                                  />
                                </Tooltip>
                              )}
                              {viewAchievementPercentageIsEnabled() && (
                                <Tooltip
                                  message={
                                    editAchievementPercentageIsEnabled()
                                      ? ACHIEVEMENT_PERCENTAGE_ENABLED_TOOLTIP
                                      : ACHIEVEMENT_PERCENTAGE_DISABLED_TOOLTIP
                                  }
                                  hide={editAchievementPercentageIsEnabled()}
                                >
                                  <StyledAchievementSelect>
                                    <FormField fixedError={false}>
                                      <Controller
                                        name={`items.${index}.achievementPercent`}
                                        control={control}
                                        disabled={
                                          !editAchievementPercentageIsEnabled()
                                        }
                                        render={({ field }) => (
                                          <Select
                                            variant={SelectVariantEnum.DEFAULT}
                                            label={PERCENT_REACHED_GOALS_LABEL}
                                            options={PERCENTAGE_OPTIONS}
                                            size={InputSizes.SMALL}
                                            id={`goal-achievement-${index}`}
                                            {...field}
                                          />
                                        )}
                                      />
                                    </FormField>
                                  </StyledAchievementSelect>
                                </Tooltip>
                              )}
                              <IconManager
                                size={IconSizeEnum.SMALL}
                                color={theme.colors.purple}
                                onClick={() => toggleGoal(index)}
                                id={`goal-toggle-${index}`}
                              >
                                {isExpanded ? (
                                  <ChevronUpIcon />
                                ) : (
                                  <ChevronDownIcon />
                                )}
                              </IconManager>
                            </StyledGoalCardHeaderRight>
                          </StyledGoalCardHeader>
                          {isExpanded && (
                            <StyledGoalCardBody>
                              <FormField fixedError={false}>
                                <Controller
                                  name={`items.${index}.goal`}
                                  control={control}
                                  render={({ field }) => (
                                    <TextArea
                                      maxLength={CHARACTER_MAX_LENGTH}
                                      label={DESCRIPTION_LABEL}
                                      error={
                                        errors.items?.[index]?.goal?.message
                                      }
                                      disabled={fieldIsDisabled()}
                                      {...field}
                                    />
                                  )}
                                />
                              </FormField>
                              <StyledChallengeGrid>
                                {challengeFields.map((challenge) => (
                                  <StyledChallengeField key={challenge.key}>
                                    <StyledChallengeLabel>
                                      <BodyTyp
                                        variant="2"
                                        fontWeight={FontWeightEnum.SEMIBOLD}
                                      >
                                        {challenge.label}
                                      </BodyTyp>
                                      <Tooltip message={challenge.tooltip}>
                                        <IconManager size={IconSizeEnum.SMALL}>
                                          <InfoIcon />
                                        </IconManager>
                                      </Tooltip>
                                    </StyledChallengeLabel>
                                    <FormField fixedError={false}>
                                      <Controller
                                        name={`items.${index}.${challenge.key}`}
                                        control={control}
                                        render={({ field }) => (
                                          <Select
                                            variant={
                                              SelectVariantEnum.SINGLE_SELECT
                                            }
                                            label={challenge.label}
                                            id={`goal-challenge-${index}-${challenge.key}`}
                                            disabled={fieldIsDisabled()}
                                            options={getDimensionOptions(
                                              challenge.dimensionKey,
                                            )}
                                            error={
                                              errors.items?.[index]?.[
                                                challenge.key
                                              ]?.message
                                            }
                                            {...field}
                                            optionsContainerFitContent
                                          />
                                        )}
                                      />
                                    </FormField>
                                  </StyledChallengeField>
                                ))}
                              </StyledChallengeGrid>
                            </StyledGoalCardBody>
                          )}
                        </StyledGoalCard>
                      );
                    })}
                  </StyledGoalsList>
                </form>
              </StyledHideOrShowContainer>
              <StyledHideOrShowContainer $show={!edit}>
                <StyledGoalsList>
                  {readGoals.map((item, index) => {
                    const isExpanded = expandedGoals.includes(index);

                    return (
                      <StyledGoalCard
                        key={"row" + item.id}
                        data-testid={`goal-card-${index}`}
                      >
                        <StyledGoalCardHeader>
                          <BodyTyp
                            variant="1"
                            fontWeight={FontWeightEnum.SEMIBOLD}
                          >
                            {GOAL_LABEL} {index + 1}
                          </BodyTyp>
                          <StyledGoalCardHeaderRight>
                            {!isMyProfile && (
                              <Tooltip message={IMPACT_TOOLTIP}>
                                <Pill
                                  status="active"
                                  label={`${IMPACT_LABEL}: ${getImpactValue(
                                    item.actionChallenge,
                                    item.contestChallenge,
                                    item.timeChallenge,
                                  )}`}
                                />
                              </Tooltip>
                            )}
                            {viewAchievementPercentageIsEnabled() && (
                              <Tooltip
                                message={
                                  editAchievementPercentageIsEnabled()
                                    ? ACHIEVEMENT_PERCENTAGE_ENABLED_TOOLTIP
                                    : ACHIEVEMENT_PERCENTAGE_DISABLED_TOOLTIP
                                }
                              >
                                <StyledAchievementPillContainer>
                                  <Pill
                                    status={
                                      !editAchievementPercentageIsEnabled()
                                        ? "disabled"
                                        : showFinalCalibrationError()
                                          ? "error"
                                          : "default"
                                    }
                                    label={
                                      <>
                                        {PERCENT_REACHED_GOALS_LABEL}:{" "}
                                        {item.achievementPercent
                                          ? `${item.achievementPercent}%`
                                          : EMPTY_STRING}
                                      </>
                                    }
                                  />
                                </StyledAchievementPillContainer>
                              </Tooltip>
                            )}
                            <IconManager
                              size={IconSizeEnum.SMALL}
                              onClick={() => toggleGoal(index)}
                              id={`goal-toggle-${index}`}
                            >
                              {isExpanded ? (
                                <ChevronUpIcon />
                              ) : (
                                <ChevronDownIcon />
                              )}
                            </IconManager>
                          </StyledGoalCardHeaderRight>
                        </StyledGoalCardHeader>
                        {isExpanded && (
                          <StyledGoalCardBody>
                            <TextAreaBox text={item.goal} />
                            <StyledChallengeGrid>
                              {challengeFields.map((challenge) => (
                                <StyledChallengeField key={challenge.key}>
                                  <StyledChallengeLabel>
                                    <BodyTyp
                                      variant="2"
                                      fontWeight={FontWeightEnum.SEMIBOLD}
                                    >
                                      {challenge.label}
                                    </BodyTyp>
                                    <Tooltip message={challenge.tooltip}>
                                      <IconManager size={IconSizeEnum.SMALL}>
                                        <InfoIcon />
                                      </IconManager>
                                    </Tooltip>
                                  </StyledChallengeLabel>
                                  <BodyTyp
                                    variant="2"
                                    fontWeight={FontWeightEnum.MEDIUM}
                                  >
                                    {getEvaluationLabel(
                                      challenge.dimensionKey,
                                      item[
                                        `${challenge.key}Challenge` as const
                                      ],
                                    )}
                                  </BodyTyp>
                                </StyledChallengeField>
                              ))}
                            </StyledChallengeGrid>
                          </StyledGoalCardBody>
                        )}
                      </StyledGoalCard>
                    );
                  })}
                </StyledGoalsList>
              </StyledHideOrShowContainer>
            </StyledGoalsScroll>
            {showBottomBar && (
              <StyledBottomDivTable
                $showAchievementPercentage={viewAchievementPercentageIsEnabled()}
              >
                {viewAchievementPercentageIsEnabled() ? (
                  <StyledGoalsErrorWrapper>
                    {errors.items?.root?.message && (
                      <CaptionsTyp
                        fontWeight={FontWeightEnum.REGULAR}
                        color={theme.colors.red_1}
                      >
                        {errors.items.root.message}
                      </CaptionsTyp>
                    )}
                    {showFinalCalibrationError() && (
                      <CaptionsTyp
                        fontWeight={FontWeightEnum.REGULAR}
                        color={theme.colors.red_1}
                      >
                        <StyledFinalCalibrationError>
                          {FINAL_CALIBRATION_ERROR_START}
                          {finalDimensionPercentage}
                          {FINAL_CALIBRATION_ERROR_END}
                        </StyledFinalCalibrationError>
                      </CaptionsTyp>
                    )}
                  </StyledGoalsErrorWrapper>
                ) : null}
                <StatCard
                  items={[
                    ...(isOutOfRange()
                      ? []
                      : [
                          {
                            icon: (
                              <IconManager
                                size={IconSizeEnum.MEDIUM}
                                color={theme.colors.purple}
                              >
                                <ImpactIcon />
                              </IconManager>
                            ),
                            value: !isMyProfile ? totalImpact : "",
                            label: (
                              <BodyTyp
                                variant="2"
                                fontWeight={FontWeightEnum.MEDIUM}
                                color={theme.colors.purple}
                              >
                                {getImpactRangeLabel(totalImpact)}
                              </BodyTyp>
                            ),
                            tooltip: getImpactRangeDescription(totalImpact),
                          },
                        ]),
                    ...(viewAchievementPercentageIsEnabled()
                      ? [
                          {
                            icon: (
                              <IconManager
                                size={IconSizeEnum.MEDIUM}
                                color={theme.colors.purple}
                              >
                                <TargetIcon />
                              </IconManager>
                            ),
                            value: getAvgAchievementPercentage(),
                            label: AVG_ACHIEVEMENT_LABEL,
                            error: edit ? false : showFinalCalibrationError(),
                          },
                        ]
                      : []),
                  ]}
                />
              </StyledBottomDivTable>
            )}
          </StyledGoalsPage>
        </Loader>
      </StyledHideOrShowContainer>
    </>
  );
};
